import React, { useCallback, useState, useMemo } from "react";

import {
  AHIcon,
  AHText,
  Circle,
  EmployeeAvatar,
  ListItem,
  TouchableBox,
  useBoxStyle,
  useLayoutContext,
  useTime,
  AHSeparator,
  Box,
  LegacyFilter,
} from "arkh";

import { SearchInput } from "../Products/SearchInput";
import { FlashList } from "@shopify/flash-list";
import { keyExtractorId } from "@/src/common/keyExtractors";
import { ActionFooter } from "@/src/components/ActionFooter";
import { graphql, useFragment, useMutation } from "react-relay";

import { UserAdminAccounts_store$key } from "./__generated__/UserAdminAccounts_store.graphql";
import { useGetRoleLabels } from "./RoleLabels";
import { FlatList, Platform } from "react-native";
import { useIntlTokens } from "@/src/components/mobile/useIntlTokens";

import { FormattedMessage } from "react-intl";
import { RoleTag } from "./RoleTag";
import { DateTime } from "luxon";
import { UserAdminAccountsMissingEmployee_storeUser$key } from "./__generated__/UserAdminAccountsMissingEmployee_storeUser.graphql";
import { UserAdminAccountsMissingEmployeeLinkEmployeeMutation } from "./__generated__/UserAdminAccountsMissingEmployeeLinkEmployeeMutation.graphql";
import { KeyboardAwareFlashListScrollComponent } from "@/src/components/mobile/KeyboardAwareFlashListScrollComponent";
import { useSafeAreaInsets } from "react-native-safe-area-context";

function MissingEmployee(props: {
  user: UserAdminAccountsMissingEmployee_storeUser$key;
}) {
  const [linkEmployee] =
    useMutation<UserAdminAccountsMissingEmployeeLinkEmployeeMutation>(graphql`
      mutation UserAdminAccountsMissingEmployeeLinkEmployeeMutation(
        $id: ID!
        $employeeId: ID!
      ) {
        store {
          storeUser(id: $id) {
            linkEmployee(employeeId: $employeeId) {
              id
              employee {
                id
                name
                ...Avatar_employee
              }
            }
          }
        }
      }
    `);

  const user = useFragment(
    graphql`
      fragment UserAdminAccountsMissingEmployee_storeUser on StoreUser {
        id
        employeeSuggestion {
          name
          ...Avatar_employee
          id
        }
      }
    `,
    props.user
  );

  function onAccept() {
    if (user.employeeSuggestion) {
      linkEmployee({
        variables: {
          id: user.id,
          employeeId: user.employeeSuggestion?.id,
        },
      });
    }
  }

  if (user.employeeSuggestion) {
    return (
      <Box spaceRight="small">
        <Box
          style={{ flexDirection: "row", alignItems: "center", borderWidth: 1 }}
          color="surface-background-light"
          borderColor="border-primary"
          cornerRadius="large"
          space="small"
        >
          <EmployeeAvatar employee={user.employeeSuggestion} size="small" />
          <AHText
            type="subtitle"
            color="text-secondary"
            spaceLeft="small"
            spaceRight="small"
          >
            {user.employeeSuggestion.name}
          </AHText>
          <TouchableBox
            cornerRadius="large"
            color="primary"
            space="small"
            onPress={onAccept}
          >
            <AHText type="subtitle" color="text-inverted">
              Accept
            </AHText>
          </TouchableBox>
        </Box>
      </Box>
    );
  }

  return (
    <AHText type="subtitle" color="negative" spaceRight="medium">
      <FormattedMessage
        defaultMessage="Missing employee"
        description="UserAdminAccounts missing employee"
      />
    </AHText>
  );
}
export function UserAdminAccounts(props: {
  store: UserAdminAccounts_store$key;
  openAccount(id: string): void;
  openAccountInvite(type: "GROUP" | "INDIVIDUAL"): void;
}) {
  const intl_tokens = useIntlTokens();
  const onOpen = () => {
    props.openAccountInvite("INDIVIDUAL");
  };
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<
    "CO_WORKER" | "MANAGER" | "ADMIN" | "COWORKER_GROUP" | null
  >(null);
  const [isSearching, setIsSearching] = useState(false);

  const store = useFragment(
    graphql`
      fragment UserAdminAccounts_store on Store {
        users {
          id
          user {
            id
            name
            email
            invite {
              expiresAt
              createdAt
            }
          }
          role
          ...UserAdminAccountsMissingEmployee_storeUser

          employee {
            id
            name
            ...Avatar_employee
          }
        }
      }
    `,
    props.store
  );

  const listData = useMemo(() => {
    return store.users
      .filter((u) => {
        if (query !== "") {
          if (
            u.user?.email
              ?.toLocaleLowerCase()
              .includes(query.toLocaleLowerCase())
          ) {
            return true;
          }
          if (
            u.employee?.name
              ?.toLocaleLowerCase()
              .includes(query.toLocaleLowerCase())
          ) {
            return true;
          }
          return false;
        } else {
          return filter === null || filter === u.role;
        }
      })
      .sort((a, b) => (a.user?.invite ? 0 : 1) - (b.user?.invite ? 0 : 1));
  }, [filter, query, store.users]);

  const getRoleLabel = useGetRoleLabels();
  const now = useTime();
  const renderItem = useCallback(
    ({ item }: { item: (typeof listData)[number] }) => {
      const expiredInvite = item.user?.invite?.expiresAt
        ? DateTime.fromISO(item.user.invite.expiresAt) < now
        : false;

      const inviteSentAt = item.user?.invite?.createdAt
        ? DateTime.fromISO(item.user.invite.createdAt).toLocaleString({
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
          })
        : "";

      function getFooter() {
        if (item.employee) {
          return (
            <Box
              style={{
                flexDirection: "row",
                alignItems: "center",
              }}
              spaceRight="medium"
            >
              <EmployeeAvatar employee={item.employee} size="small" />
              <AHText type="subtitle" spaceLeft="small" color="text-secondary">
                {item.employee.name}
              </AHText>
            </Box>
          );
        }

        if (item.role === "COWORKER_GROUP") {
          return (
            <Box
              style={{
                flexDirection: "row",
                alignItems: "center",
              }}
              spaceRight="medium"
            >
              <AHIcon type="group-actor" color="text-secondary" size="small" />
              <AHText type="subtitle" spaceLeft="xsmall" color="text-secondary">
                {intl_tokens.GroupAccount}
              </AHText>
            </Box>
          );
        }

        if (item?.user?.invite) {
          return (
            <Box style={{ flexDirection: "row", alignItems: "center" }}>
              {item.employee ? null : <MissingEmployee user={item} />}
              <Circle
                size="dot"
                color={expiredInvite ? "negative" : "semiNegative"}
              />
              <AHText type="subtitle" color="text-secondary" spaceLeft="small">
                {expiredInvite
                  ? intl_tokens.InviteExpired
                  : intl_tokens.InviteSent + " " + inviteSentAt}
              </AHText>
            </Box>
          );
        }

        return <MissingEmployee user={item} />;
      }

      const footer = getFooter();

      return (
        <ListItem
          right={
            <Box style={{ flexDirection: "row", alignItems: "center" }}>
              {item.role !== "COWORKER_GROUP" ? (
                <Box spaceRight="small">
                  <RoleTag role={item.role} />
                </Box>
              ) : null}
              <ListItem.NavArrow />
            </Box>
          }
          footer={
            footer ? (
              <Box
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  width: "100%",
                }}
              >
                {footer}
              </Box>
            ) : null
          }
          onPress={() => {
            props.openAccount(item.id);
          }}
        >
          <AHText
            type="list-item-title"
            color={!item.user?.invite ? "text-primary" : "text-secondary"}
          >
            {item.user?.email}
            {item.user?.name &&
            item.user.name.trim().length > 2 &&
            !item?.employee
              ? ` (${item.user.name})`
              : ""}
          </AHText>
        </ListItem>
      );
    },
    [
      intl_tokens.GroupAccount,
      intl_tokens.InviteExpired,
      intl_tokens.InviteSent,
      now,
      props,
    ]
  );
  const safeAreaInsets = useSafeAreaInsets();
  const layout = useLayoutContext();

  const filters = useMemo(() => {
    const f: ("ADMIN" | "MANAGER" | "CO_WORKER" | "COWORKER_GROUP")[] = [
      "ADMIN",
      "MANAGER",
      "CO_WORKER",
      "COWORKER_GROUP",
    ];

    return f.map((value) => ({
      value,
      label: getRoleLabel(value),
      selected: value === filter,
    }));
  }, [filter, getRoleLabel]);

  const contentContainerStyle = useBoxStyle({
    color: "surface-primary",
  });
  return (
    <>
      <Box color="surface-primary">
        <SearchInput
          query={query}
          isSearching={isSearching}
          onChange={(e) => {
            setQuery(e.nativeEvent.text);
          }}
          onFocus={() => {
            setFilter(null);
            setIsSearching(true);
          }}
          onBlur={() => {
            setQuery("");
            setIsSearching(false);
          }}
          showCancel={true}
          returnKeyType="done"
          hideSeparator
        />
      </Box>
      <AHSeparator />
      {isSearching ? null : (
        <>
          <LegacyFilter
            filters={filters}
            onPressFilter={(filter) => {
              if (filter.selected) {
                setFilter(null);
              } else {
                setFilter(filter.value);
              }
            }}
          />
          <AHSeparator />
        </>
      )}
      {Platform.OS === "web" ? (
        <FlatList
          data={listData}
          renderItem={renderItem}
          keyExtractor={keyExtractorId}
          ItemSeparatorComponent={AHSeparator}
          contentContainerStyle={contentContainerStyle}
          keyboardShouldPersistTaps="handled"
        />
      ) : (
        <FlashList
          data={listData}
          renderItem={renderItem}
          keyExtractor={keyExtractorId}
          ItemSeparatorComponent={AHSeparator}
          keyboardShouldPersistTaps="handled"
          renderScrollComponent={KeyboardAwareFlashListScrollComponent}
          contentContainerStyle={{
            paddingBottom: layout.space.medium + safeAreaInsets.bottom,
          }}
        />
      )}

      <ActionFooter
        primary={
          <ActionFooter.Primary
            title={
              <FormattedMessage
                defaultMessage="Add Account"
                description="UserAdminAccounts add account"
              />
            }
            onPress={() => {
              onOpen();
            }}
            icon="add"
          />
        }
      />
    </>
  );
}
